import { httpClient, resolveHttpResourceUrl } from "../../../lib/http/axios";

const PDF_REQUEST_TIMEOUT_MS = 180_000;
const WINDOWS_RESERVED_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;

/** 请求 Python Chromium 生成后台行程 PDF 并下载。 */
export async function downloadItineraryPdf(itineraryId: string, fileName: string, includeDayImages = true): Promise<void> {
  const pdf = await requestPdf(`/itineraries/${encodeURIComponent(itineraryId)}/pdf`, fileName, includeDayImages);
  downloadPdf(pdf.blob, pdf.fileName);
}

/** 请求 Python Chromium 生成公开分享行程 PDF 并下载。 */
export async function downloadPublicItineraryPdf(token: string, fileName: string): Promise<void> {
  openPublicPdf(token, fileName);
}

/** 从受控 Python 接口请求 PDF 二进制。 */
async function requestPdf(
  path: string,
  fileName: string,
  includeDayImages: boolean,
): Promise<{ blob: Blob; fileName: string }> {
  const response = await httpClient.post<Blob>(path, undefined, {
    params: { file_name: fileName, ...(!includeDayImages ? { include_day_images: false } : {}) },
    responseType: "blob",
    timeout: PDF_REQUEST_TIMEOUT_MS,
    "axios-retry": { retries: 0 },
  });
  const disposition = String(response.headers?.["content-disposition"] ?? "");
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encodedName) {
    try {
      fileName = decodeURIComponent(encodedName.replace(/^"|"$/g, ""));
    } catch {
      // 无法解码时沿用调用方文件名，仍由下载入口清理非法字符。
    }
  }
  return { blob: response.data, fileName };
}

/** 公开分享通过浏览器原生附件地址下载，不依赖 Blob 隐藏帧。 */
function openPublicPdf(token: string, fileName: string): void {
  const query = new URLSearchParams({
    file_name: fileName,
    inline: "false",
  });
  const path = `/itineraries/public/${encodeURIComponent(token)}/pdf?${query.toString()}`;
  const anchor = document.createElement("a");
  anchor.href = resolveHttpResourceUrl(path);
  anchor.hidden = true;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
  }
}

/** 使用一次性对象 URL 触发浏览器下载。 */
function downloadPdf(pdf: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(pdf);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = normalizePdfFileName(fileName);
  anchor.hidden = true;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }
}

/** 保持下载属性与后端响应头一致地生成本地安全文件名。 */
function normalizePdfFileName(fileName: string): string {
  const withoutControls = Array.from(fileName.trim(), (character) =>
    character.charCodeAt(0) < 32 ? "_" : character).join("");
  const normalized = withoutControls
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/\.pdf$/i, "")
    .replace(/[ .]+$/g, "");
  const shortened = Array.from(normalized).slice(0, 120).join("").replace(/[ .]+$/g, "");
  const safeName = WINDOWS_RESERVED_NAME.test(shortened) ? `_${shortened}` : shortened;
  return `${safeName || "客户行程"}.pdf`;
}
